import React, { useEffect, useRef, useState, forwardRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { COLORS_ADMIN_PAGE, COLORS_CONFIG_PAGE } from '../../conf/constants';
import { TRANSACTION_SIDEBAR_CONSTANTS } from '../constants/transactionSidebar';
import { User } from '../../user/types/type';

interface UserSidebarProps {
  users: User[];
  selectedUser: User | null;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onUserSelect: (user: User) => void;
  currentPage: number;
  totalUsers: number;
  usersPerPage: number;
  onPageChange: (page: number) => void;
  onUsersPerPageChange?: (usersPerPage: number) => void;
}

const UserSidebar = forwardRef<HTMLDivElement, UserSidebarProps>(({
  users,
  selectedUser,
  searchTerm,
  onSearchChange,
  onUserSelect,
  currentPage,
  totalUsers,
  usersPerPage,
  onPageChange,
  onUsersPerPageChange,
}, ref) => {
  const userListRef = useRef<HTMLDivElement>(null);
  const [calculatedUsersPerPage, setCalculatedUsersPerPage] = useState(usersPerPage);

  // Function to calculate the number of items that can be displayed
  const calculateUsersPerPage = () => {
    if (!userListRef.current) return usersPerPage;

    const containerHeight = userListRef.current.clientHeight;
    const availableHeight = containerHeight - TRANSACTION_SIDEBAR_CONSTANTS.PADDING_BUFFER;
    const calculatedItems = Math.floor(availableHeight / TRANSACTION_SIDEBAR_CONSTANTS.ITEM_HEIGHT) - 1;
    
    // Ensure at least MIN_ITEMS are displayed
    return Math.max(TRANSACTION_SIDEBAR_CONSTANTS.MIN_ITEMS, calculatedItems);
  };

  // Hook to monitor size changes
  useEffect(() => {
    const updateUsersPerPage = () => {
      const newUsersPerPage = calculateUsersPerPage();
      if (newUsersPerPage !== calculatedUsersPerPage && onUsersPerPageChange) {
        setCalculatedUsersPerPage(newUsersPerPage);
        onUsersPerPageChange(newUsersPerPage);
      }
    };

    // Add small delay to ensure DOM has rendered
    const timeoutId = setTimeout(updateUsersPerPage, TRANSACTION_SIDEBAR_CONSTANTS.INITIAL_CALCULATION_DELAY);

    // Monitor window resize events
    const handleResize = () => {
      clearTimeout(timeoutId);
      setTimeout(updateUsersPerPage, TRANSACTION_SIDEBAR_CONSTANTS.RESIZE_DEBOUNCE_DELAY);
    };

    window.addEventListener('resize', handleResize);
    
    // Monitor container size changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (userListRef.current) {
      resizeObserver.observe(userListRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [calculatedUsersPerPage, onUsersPerPageChange]);

  const totalPages = Math.ceil(totalUsers / usersPerPage);

  const handlePageChange = (page: number) => {
    onPageChange(Math.max(1, Math.min(page, totalPages)));
  };

  const handleFastPageChange = (jump: number) => {
    const newPage = currentPage + jump * TRANSACTION_SIDEBAR_CONSTANTS.FAST_PAGE_JUMP;
    onPageChange(Math.max(1, Math.min(newPage, totalPages)));
  };

  return (
    <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {TRANSACTION_SIDEBAR_CONSTANTS.TITLE}
        </Typography>
        <TextField
          size="small"
          placeholder={TRANSACTION_SIDEBAR_CONSTANTS.SEARCH_PLACEHOLDER}
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: TRANSACTION_SIDEBAR_CONSTANTS.SEARCH_FIELD.MARGIN_BOTTOM,
            bgcolor: COLORS_CONFIG_PAGE.background,
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
              '&:focus': {
                bgcolor: COLORS_CONFIG_PAGE.primary,
                '& .MuiInputBase-input': {
                  color: COLORS_CONFIG_PAGE.white,
                },
              },
            },
            borderRadius: TRANSACTION_SIDEBAR_CONSTANTS.SEARCH_FIELD.BORDER_RADIUS,
            p: TRANSACTION_SIDEBAR_CONSTANTS.SEARCH_FIELD.PADDING,
          }}
        />
      </Box>

      {/* Users List */}
      <Box 
        ref={userListRef}
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 1, 
          minHeight: 0 // Important: allows flex item to shrink
        }}
      >
        {users.map((user) => (
          <Box
            key={user.userId}
            onClick={() => onUserSelect(user)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              cursor: 'pointer',
              borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
              bgcolor: selectedUser?.userId === user.userId
                ? COLORS_ADMIN_PAGE.PRIMARY
                : COLORS_ADMIN_PAGE.LIGHT_GRAY,
              color: selectedUser?.userId === user.userId
                ? COLORS_ADMIN_PAGE.WHITE
                : COLORS_ADMIN_PAGE.BLACK,
              mb: 1,
              borderRadius: '4px',
              height: `${TRANSACTION_SIDEBAR_CONSTANTS.ITEM_HEIGHT}px`, // Fixed height based on constant
              boxSizing: 'border-box',
              '&:hover': {
                bgcolor: selectedUser?.userId === user.userId
                  ? COLORS_ADMIN_PAGE.PRIMARY
                  : COLORS_ADMIN_PAGE.BACKGROUND,
              },
            }}
          >
            <Avatar 
              src={user.profileImageUrl || ''} 
              sx={{ 
                width: TRANSACTION_SIDEBAR_CONSTANTS.AVATAR.WIDTH, 
                height: TRANSACTION_SIDEBAR_CONSTANTS.AVATAR.HEIGHT, 
                mr: TRANSACTION_SIDEBAR_CONSTANTS.AVATAR.MARGIN_RIGHT 
              }} 
            />
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap' // Prevent text wrapping
                }}
              >
                {user.firstName} {user.middleName} {user.lastName}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
          borderTop: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
          bgcolor: COLORS_ADMIN_PAGE.WHITE,
          flexShrink: 0,
        }}
      >
        <Box>
          <Button
            variant="contained"
            onClick={() => handleFastPageChange(-1)}
            disabled={currentPage <= TRANSACTION_SIDEBAR_CONSTANTS.FAST_PAGE_JUMP}
            sx={{ 
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
              mr: 0.5, 
              minWidth: TRANSACTION_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              px: TRANSACTION_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
            }}
          >
            <KeyboardDoubleArrowLeft />
          </Button>
          <Button
            variant="contained"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            sx={{ 
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
              mr: 1, 
              minWidth: TRANSACTION_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              px: TRANSACTION_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
            }}
          >
            <KeyboardArrowLeft />
          </Button>
          </Box>
        
        <Typography variant="body2" sx={{ color: COLORS_ADMIN_PAGE.TEXT_SECONDARY, mx: 1 }}>
          {currentPage} of {totalPages}
        </Typography>
        
        <Box>
          <Button
            variant="contained"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            sx={{ 
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
              ml: 1, 
              minWidth: TRANSACTION_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              px: TRANSACTION_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
            }}
          >
            <KeyboardArrowRight />
          </Button>
          <Button
            variant="contained"
            onClick={() => handleFastPageChange(1)}
            disabled={currentPage >= totalPages - TRANSACTION_SIDEBAR_CONSTANTS.FAST_PAGE_JUMP + 1}
            sx={{ 
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
              ml: 0.5, 
              minWidth: TRANSACTION_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              px: TRANSACTION_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
            }}
          >
            <KeyboardDoubleArrowRight />
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

UserSidebar.displayName = 'UserSidebar';

export default UserSidebar;